/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import { GlobalConstants } from '../../../../feature/src/lib/constants/global-constants';
import { Observable, from, of } from 'rxjs';
import {
  Param,
  ParamSize,
} from '../../../../feature/src/lib/components/add-family-information/add-family-information-types';
import { ParamCategory } from '../../../../feature/src/lib/models/add-project-parameter-category';
import { DotNetRequestMessageService } from '@mep/web-browser-bridge';

import { ProjectParameterDataItems } from '@hcworkspace/sysque/toolbelt/feature';
import { ProjectDataParamInfo } from '../../../../feature/src/lib/models/add-project-data-params-info';
import { addProjectParameterData } from '../../../../data-access/src/lib/domain/data/add-project-parameter-data';
import { ProjectParameterInfo } from 'libs/sysque/toolbelt/feature/src/lib/models/add-project-parameter-info';

@Injectable({
  providedIn: 'root',
})

/**
 * Represents a service for handling project data events.
 */
export class ProjectDataService {
  constructor(public messageHandler: DotNetRequestMessageService) {}

  /**
   * Adds a new project parameter.
   *
   * @param projectParameterInfo - The information of the project parameter to be added.
   */
  public addProjectParameter(projectParameterInfo: ProjectParameterInfo): Observable<void> {
    return from<Promise<void>>(this.messageHandler.sendRequestAsync(GlobalConstants.AddProjectParameter, [projectParameterInfo]));
  }

  /**
   * Retrieves the definition group parameters.
   * @returns An Observable that emits an array of ProjectDataParamInfo objects.
   */
  public getDefinitionGroupParameters(): Observable<ProjectDataParamInfo[]> {
    return from<Promise<ProjectDataParamInfo[]>>(this.messageHandler.sendRequestAsync(GlobalConstants.GetDefinitionGroupParameters));
  }

  /**
   * Retrieves the built-in group parameters.
   * @returns An Observable that emits an array of ProjectDataParamInfo objects representing the built-in group parameters.
   */
  public getBuiltInGroupParameters(): Observable<ProjectDataParamInfo[]> {
    return from<Promise<ProjectDataParamInfo[]>>(this.messageHandler.sendRequestAsync(GlobalConstants.GetBuiltInGroupParameters));
  }

  /**
   * Initializes the add project parameters from JSON.
   * @returns An observable that emits an array of project parameter data items.
   */
  public initializeAddProjectParametersFromJson(): Observable<ProjectParameterDataItems[]> {
    const result: ProjectParameterDataItems[] = [{ projectParameterDataItems: addProjectParameterData }];
    return of(result);
  }

  /**
   * Retrieves the parameter categories based on the current category.
   * @param currentCategory The current category.
   * @returns An Observable that emits an array of ParamCategory objects.
   */
  public getParamCategory(currentCategory: string): Observable<ParamCategory[]> {
    return from<Promise<ParamCategory[]>>(this.messageHandler.sendRequestAsync(GlobalConstants.GetParamCategories, [currentCategory]));
  }

  /**
   * Reads parameters from the Revit document for a selected category.
   * @param selectedCategory - The category for which parameters need to be read.
   * @returns An Observable that emits an array of Param objects.
   */
  public readParametersFromDocument(selectedCategory: string): Observable<Param[]> {
    return from<Promise<Param[]>>(this.messageHandler.sendRequestAsync(GlobalConstants.ReadParametersFromDocument, [selectedCategory]));
  }

  /**
   * Handles the click event when modifying a menu item and opens the assign parameter popup.
   *
   * @param height - The height of the menu item.
   * @param width - The width of the menu item.
   */
  public onModifyMenuItemClick(dialogRoute: string, height: number, width: number): Observable<any> {
    return from<Promise<any>>(this.messageHandler.sendRequestAsync(GlobalConstants.OnModifyMenuItemClick, [dialogRoute, height, width]));
  }

  /**
   * Changes the dimensions of the dialog.
   *
   * @param height - The new height of the dialog.
   * @returns An observable that emits 'OK' when the dimensions are successfully changed.
   */
  public changeDialogDimensions(height: number): Observable<any> {
    return from<Promise<any>>(this.messageHandler.sendRequestAsync(GlobalConstants.ChangeDialogDimensions, [height]));
  }

  /**
   * Handles the click event for importing the parameters sizes from Excel.
   * @param parametersSizes - An array of ParamSize objects.
   */
  public importExcelClick(parametersSizes: ParamSize[]): Observable<any> {
    return from<Promise<any>>(this.messageHandler.sendRequestAsync(GlobalConstants.ImportExcelClick, [parametersSizes]));
  }

  /**
   * Exports the parameters sizes to an Excel file.
   *
   * @param parametersSizes - An array of ParamSize objects.
   * @param fileName - The name of the Excel file.
   */
  public exportExcelClick(parametersSizes: ParamSize[], fileName: string): Observable<any> {
    return from<Promise<any>>(this.messageHandler.sendRequestAsync(GlobalConstants.ExportExcelClick, [parametersSizes, fileName]));
  }

  /**
   * Handles the logic when the window is closed.
   */
  public onCloseWindow(): Observable<any> {
    return from<Promise<any>>(this.messageHandler.sendRequestAsync(GlobalConstants.OnCloseWindow));
  }
}
